/**
 * Field Validators
 *
 * Built-in validation for collect_info workflow fields.
 * Runs on the frontend (on blur) and as a safety net on the backend.
 */

// ──────────────────────────────────────────────
// Valid US area codes (source: NANPA assignments)
// ──────────────────────────────────────────────
const VALID_AREA_CODES = new Set([
  '201','202','203','204','205','206','207','208','209','210','212','213','214','215',
  '216','217','218','219','220','223','224','225','226','228','229','231','234','236',
  '239','240','248','249','250','251','252','253','254','256','260','262','267','269',
  '270','272','274','276','278','279','281','283','289','301','302','303','304','305',
  '307','308','309','310','312','313','314','315','316','317','318','319','320','321',
  '323','325','326','327','330','331','332','334','336','337','339','340','341','346',
  '347','351','352','360','361','364','369','380','385','386','401','402','403','404',
  '405','406','407','408','409','410','412','413','414','415','416','417','418','419',
  '423','424','425','430','431','432','434','435','437','438','440','442','443','445',
  '447','448','450','458','463','464','469','470','472','475','478','479','480','484',
  '501','502','503','504','505','506','507','508','509','510','512','513','514','515',
  '516','517','518','519','520','530','531','534','539','540','541','548','551','557',
  '559','561','562','563','564','567','570','571','572','573','574','575','579','580',
  '581','582','585','586','587','601','602','603','604','605','606','607','608','609',
  '610','612','613','614','615','616','617','618','619','620','623','624','626','628',
  '629','630','631','636','639','640','641','646','647','649','650','651','656','657',
  '659','660','661','662','667','669','670','671','672','678','680','681','682','683',
  '684','689','701','702','703','704','705','706','707','708','709','710','712','713',
  '714','715','716','717','718','719','720','721','724','725','726','727','730','731',
  '732','734','737','740','742','743','747','754','757','760','762','763','765','769',
  '770','771','772','773','774','775','778','779','780','781','782','784','785','786',
  '787','801','802','803','804','805','806','807','808','809','810','812','813','814',
  '815','816','817','818','819','820','825','828','829','830','831','832','838','839',
  '840','843','845','847','848','849','850','854','856','857','858','859','860','862',
  '863','864','865','867','868','869','870','872','873','878','901','902','903','904',
  '905','906','907','908','909','910','912','913','914','915','916','917','918','919',
  '920','925','928','929','930','931','934','935','936','937','938','939','940','941',
  '943','945','947','948','949','951','952','954','956','959','970','971','972','973',
  '975','978','979','980','984','985','986','989',
]);

// ──────────────────────────────────────────────
// Valid TLDs for email domain verification
// ──────────────────────────────────────────────
const VALID_TLDS = new Set([
  'com', 'org', 'net', 'edu', 'gov', 'mil', 'int',
  'io', 'co', 'us', 'uk', 'ca', 'de', 'fr', 'au', 'in', 'jp', 'cn', 'br',
  'it', 'nl', 'es', 'se', 'no', 'dk', 'fi', 'ch', 'at', 'be', 'ie', 'nz',
  'za', 'mx', 'ar', 'cl', 'pe', 'kr', 'tw', 'sg', 'hk', 'my', 'th', 'ph',
  'id', 'vn', 'ae', 'sa', 'il', 'ru', 'pl', 'cz', 'ro', 'hu', 'pt', 'gr',
  'info', 'biz', 'name', 'pro', 'aero', 'coop', 'museum',
  'app', 'dev', 'tech', 'ai', 'me', 'tv', 'cc', 'xyz', 'online', 'site',
  'store', 'cloud', 'design', 'agency', 'digital', 'media', 'studio',
]);

// Valid US state codes
const US_STATE_CODES = new Set([
  'AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN',
  'IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH',
  'NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT',
  'VT','VA','WA','WV','WI','WY',
]);

// ──────────────────────────────────────────────
// Email Validator
// ──────────────────────────────────────────────
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function validateEmail(value) {
  if (!value) return { valid: false, message: 'Email is required' };

  if (!EMAIL_REGEX.test(value)) {
    return { valid: false, message: 'Enter a valid email address' };
  }

  // Check that the domain has a plausible TLD
  const domain = value.split('@')[1];
  if (!domain) return { valid: false, message: 'Enter a valid email address' };

  const parts = domain.split('.');
  const tld = parts[parts.length - 1].toLowerCase();

  if (!VALID_TLDS.has(tld)) {
    return { valid: false, message: `"${domain}" doesn't appear to be a valid email domain` };
  }

  // Check domain has at least 2 parts (name + tld)
  if (parts.length < 2 || parts[0].length === 0) {
    return { valid: false, message: 'Enter a valid email domain' };
  }

  return { valid: true };
}

// ──────────────────────────────────────────────
// Phone Validator (US)
// ──────────────────────────────────────────────
export function validatePhone(value) {
  if (!value) return { valid: false, message: 'Phone number is required' };

  // Strip formatting
  const digits = value.replace(/\D/g, '');

  // Handle leading 1 (country code)
  const normalized = digits.length === 11 && digits[0] === '1' ? digits.slice(1) : digits;

  if (normalized.length !== 10) {
    return { valid: false, message: 'Enter a 10-digit US phone number' };
  }

  const areaCode = normalized.slice(0, 3);
  if (!VALID_AREA_CODES.has(areaCode)) {
    return { valid: false, message: `"${areaCode}" is not a valid US area code` };
  }

  return { valid: true };
}

// ──────────────────────────────────────────────
// State Validator
// ──────────────────────────────────────────────
export function validateState(value) {
  if (!value) return { valid: false, message: 'State is required' };
  if (!US_STATE_CODES.has(value.toUpperCase())) {
    return { valid: false, message: 'Enter a valid US state code' };
  }
  return { valid: true };
}

// ──────────────────────────────────────────────
// Generic Field Validator
// ──────────────────────────────────────────────
export function validateField(value, field) {
  const val = value?.toString().trim() || '';

  // Required check
  if (field.required && !val) {
    return { valid: false, message: `${field.label} is required` };
  }

  // Skip further validation if empty and not required
  if (!val) return { valid: true };

  // Built-in type validators
  switch (field.type) {
    case 'email':
      return validateEmail(val);
    case 'phone':
      return validatePhone(val);
    case 'state':
      return validateState(val);
    default:
      break;
  }

  // Custom regex validation
  if (field.validation?.pattern) {
    try {
      if (!new RegExp(field.validation.pattern).test(val)) {
        return { valid: false, message: field.validation.message || `Invalid ${field.label}` };
      }
    } catch {
      // Invalid regex — skip
    }
  }

  // Min/max length
  if (field.validation?.minLength && val.length < field.validation.minLength) {
    return { valid: false, message: `${field.label} must be at least ${field.validation.minLength} characters` };
  }
  if (field.validation?.maxLength && val.length > field.validation.maxLength) {
    return { valid: false, message: `${field.label} must be at most ${field.validation.maxLength} characters` };
  }

  return { valid: true };
}
